import React from "react";
import { MaskReveal, Reveal } from "@/components/Reveal";
import { useSEO } from "@/lib/api";

const PolicyLayout = ({ testid, title, updated, sections }) => (
  <div data-testid={testid} className="pt-32 pb-28">
    <div className="max-w-[900px] mx-auto px-6 md:px-10">
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Legal</p>
      <h1 className="font-display text-5xl md:text-7xl tracking-[-0.03em] leading-[0.9] mb-4">
        <MaskReveal>{title}</MaskReveal>
      </h1>
      <p className="text-sm text-muted-foreground mb-14">Last updated {updated}</p>
      <div className="space-y-10">
        {sections.map((s, i) => (
          <Reveal key={i} delay={i * 0.03}>
            <h2 className="font-display text-2xl tracking-tight mb-3">{s.h}</h2>
            <p className="text-muted-foreground leading-relaxed">{s.p}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

export function PrivacyPolicy() {
  useSEO("Privacy Policy", "How VYRON collects, uses and protects your personal information.");
  return (
    <PolicyLayout testid="privacy-page" title="Privacy Policy" updated="August 2025" sections={[
      { h: "Information we collect", p: "We collect the details you provide at checkout and account creation — name, email, phone, and shipping address — along with order history. We never store your full card details; payments are processed securely by our payment partners." },
      { h: "How we use your data", p: "Your information is used solely to process orders, provide support, send order updates, and — with your consent — share news of new collections. We do not sell your personal data to third parties." },
      { h: "Cookies", p: "We use essential cookies to keep you signed in and remember your bag and wishlist. Analytics cookies help us improve the experience and may be disabled in your browser." },
      { h: "Your rights", p: "You may request access to, correction of, or deletion of your personal data at any time by writing to vyronofficial6@gmail.com." },
      { h: "Contact", p: "For any privacy questions, reach our team at vyronofficial6@gmail.com, VYRON, Chandigarh, Mohali, India." },
    ]} />
  );
}

export function RefundPolicy() {
  useSEO("Refund Policy", "VYRON returns, exchanges and refund terms.");
  return (
    <PolicyLayout testid="refund-page" title="Refund Policy" updated="August 2025" sections={[
      { h: "30-day returns", p: "We accept returns of unworn frames in original condition and packaging within 30 days of delivery. Sunglasses that show signs of wear or damage are not eligible." },
      { h: "How to initiate a return", p: "Email vyronofficial6@gmail.com with your order number. Our concierge will arrange a pickup and guide you through the process." },
      { h: "Refund timeline", p: "Once your return is received and inspected, refunds are issued to your original payment method within 5–7 business days." },
      { h: "Exchanges", p: "Prefer a different style or colour? We're happy to exchange eligible items — subject to availability." },
      { h: "Non-refundable", p: "Shipping charges and limited-edition pieces marked final sale are non-refundable unless the item arrives damaged or defective." },
    ]} />
  );
}

export function ShippingPolicy() {
  useSEO("Shipping Policy", "VYRON shipping timelines, charges and international delivery.");
  return (
    <PolicyLayout testid="shipping-page" title="Shipping Policy" updated="August 2025" sections={[
      { h: "Free insured shipping", p: "Enjoy complimentary insured shipping on all orders over ₹4,999 within India. Orders below this value carry a flat ₹199 shipping fee." },
      { h: "Dispatch & delivery", p: "Orders are dispatched within 24 hours and typically delivered in 2–4 business days across India. You'll receive tracking details by email." },
      { h: "International", p: "We ship worldwide with tracked, insured delivery. Duties and taxes, where applicable, are calculated at checkout or on delivery per local regulations." },
      { h: "Order tracking", p: "Track your order anytime from the Track Order page using your order number and email." },
      { h: "Packaging", p: "Every VYRON pair ships in a signature protective case and premium packaging, ready to gift." },
    ]} />
  );
}

export function TermsConditions() {
  useSEO("Terms & Conditions", "The terms governing your use of the VYRON website and purchases.");
  return (
    <PolicyLayout testid="terms-page" title="Terms & Conditions" updated="August 2025" sections={[
      { h: "Acceptance", p: "By using this website and placing an order, you agree to these terms. Please read them carefully before purchasing." },
      { h: "Products & pricing", p: "We strive for accuracy in product descriptions and pricing. Colours may vary slightly by screen. Prices are in Indian Rupees (INR) and inclusive of applicable taxes unless stated otherwise." },
      { h: "Orders", p: "All orders are subject to acceptance and availability. We reserve the right to cancel any order and issue a full refund where necessary." },
      { h: "Intellectual property", p: "All content, imagery, and branding on this site are the property of VYRON and may not be reproduced without written permission." },
      { h: "Liability", p: "VYRON is not liable for indirect or consequential losses arising from the use of our products beyond the value of the item purchased." },
      { h: "Governing law", p: "These terms are governed by the laws of India. Any disputes fall under the jurisdiction of the courts of Chandigarh." },
    ]} />
  );
}
